import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Download, FileSpreadsheet, Upload } from "lucide-react";
import { SAMPLE_TEMPLATES, downloadCsv } from "./sampleTemplates";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { useImportProgress } from "./useImportProgress";
import { ImportProgressPanel } from "./ImportProgressPanel";

interface BankAccount {
  id: string;
  name: string;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  bankAccounts: BankAccount[];
  userId: string | null;
  onImportSuccess?: () => void;
}

type LocalFinished = null | "done" | "error";

export const ManualTransactionDialog = ({
  open,
  onOpenChange,
  bankAccounts,
  userId,
  onImportSuccess,
}: Props) => {
  const [progressId, setProgressId] = useState<string | null>(null);
  const { row: progressRow } = useImportProgress(progressId);

  const [bankAccountId, setBankAccountId] = useState("");

  const [supportedParserIds, setSupportedParserIds] = useState<Set<string>>(new Set());

  const [file, setFile] = useState<File | null>(null);

  const [loading, setLoading] = useState(false);
  const [localFinished, setLocalFinished] = useState<LocalFinished>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const dragCounterRef = useRef(0);

  // status from realtime row (when present)
  const status = (progressRow?.status || "").toLowerCase();

  // finished if DB says so OR local finished flag (fallback)
  const isDone = status === "done" || localFinished === "done";
  const isError = status === "error" || localFinished === "error";
  const isFinished = isDone || isError;

  // --------- helpers ----------
  const clearFileInput = () => {
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const resetAll = () => {
    setProgressId(null);        // this also clears progressRow because you fixed the hook ✅
    setLocalFinished(null);
    setLoading(false);
    setIsDragOver(false);
    dragCounterRef.current = 0;

    setFile(null);
    clearFileInput();

    setBankAccountId("");
  };

  // keep selected bank account (UX-friendly) when importing another file
  const resetForAnotherFile = () => {
    setProgressId(null);
    setLocalFinished(null);
    setLoading(false);
    setIsDragOver(false);
    dragCounterRef.current = 0;

    setFile(null);
    clearFileInput();

    // ✅ обычно лучше НЕ сбрасывать банк
    // setBankAccountId("");
  };

  // ✅ critical: when dialog OPENS, start from a clean UI
  // otherwise reopening shows "Imported" and disabled button
  useEffect(() => {
    if (!open) return;

    // if you prefer to keep bank selection between openings,
    // replace resetAll() with resetForAnotherFile()
    resetAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
  if (!open) return;

  let cancelled = false;

  (async () => {
    try {
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token ?? null;

      console.log("[parser-ids] env url =", import.meta.env.VITE_SUPABASE_URL);
      console.log("[parser-ids] session error =", sessionError);
      console.log("[parser-ids] has access token =", !!accessToken);
      console.log("[parser-ids] user id =", sessionData.session?.user?.id ?? null);
      console.log("[parser-ids] token preview =", accessToken ? accessToken.slice(0, 25) : null);

      const { data, error } = await supabase.functions.invoke(
  "bank-transactions-import-csv",
  {
    body: { action: "list_parsers" },
    headers: accessToken
      ? { Authorization: `Bearer ${accessToken}` }
      : undefined,
  }
);

      console.log("[parser-ids] invoke data =", data);
      console.log("[parser-ids] invoke error =", error);

      if (error) throw error;

      const ids: string[] = data?.parser_bank_account_ids ?? [];

      console.log("[parser-ids] parser ids =", ids);
      console.log("[parser-ids] bank accounts =", bankAccounts);

      if (!cancelled) setSupportedParserIds(new Set(ids));
    } catch (e) {
      console.error("[parser-ids] failed to load parser ids", e);
      if (!cancelled) setSupportedParserIds(new Set());
    }
  })();

  return () => {
    cancelled = true;
  };
}, [open]);


  const canImport = useMemo(() => {
    return Boolean(file && bankAccountId && userId) && !loading && !isFinished;
  }, [file, bankAccountId, userId, loading, isFinished]);

  const closeDialog = () => {
    if (loading) return;
    resetAll();
    onOpenChange(false);
  };

  const acceptStatementFile = (selected: File | null) => {
    if (!selected) return;

    const lowerName = selected.name.toLowerCase();
    const isSupported = [".csv", ".tsv", ".xlsx", ".xls"].some((ext) => lowerName.endsWith(ext));

    if (!isSupported) {
      toast.error("Upload a CSV, TSV, XLSX, or XLS bank statement file");
      clearFileInput();
      return;
    }

    setFile(selected);
    setProgressId(null);
    setLocalFinished(null);
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (loading) return;

    dragCounterRef.current += 1;
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
    if (dragCounterRef.current === 0) setIsDragOver(false);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = 0;
    setIsDragOver(false);

    if (loading) return;
    if (!bankAccountId) {
      toast.error("Select a bank account first");
      return;
    }

    acceptStatementFile(e.dataTransfer.files?.[0] ?? null);
  };

  const handleImport = async () => {
    if (loading || isFinished) return;

    if (!bankAccountId || !file || !userId) {
      toast.error("Select bank, CSV file, and ensure you're authenticated");
      return;
    }

    // reset previous finish state for a new run
    setLocalFinished(null);

    try {
      setLoading(true);

      // 1) create progress row
      const { data: progress, error: progressErr } = await supabase
        .from("bank_statement_import_progress")
        .insert([
          {
            user_id: userId,
            bank_account_id: bankAccountId,
            file_name: file.name,
            status: "queued",
            message: "Preparing import",
            total_rows: 0,
            processed_rows: 0,
            parsed_count: 0,
            inserted_count: 0,
            duplicates_in_file: 0,
            duplicates_in_db: 0,
          },
        ])
        .select("id")
        .single();

      if (progressErr || !progress?.id) {
        throw progressErr ?? new Error("Failed to create progress row");
      }

      setProgressId(progress.id);

      // 2) invoke function
      const formData = new FormData();
      formData.append("file", file);
      formData.append("bank_account_id", bankAccountId);
      formData.append("file_format", file.type || "text/csv");
      formData.append("user_id", userId);
      formData.append("submission_time", new Date().toISOString());
      formData.append("progress_id", progress.id);


      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
const accessToken = sessionData.session?.access_token ?? null;

console.log("[import] env url =", import.meta.env.VITE_SUPABASE_URL);
console.log("[import] session error =", sessionError);
console.log("[import] has access token =", !!accessToken);
console.log("[import] user id from session =", sessionData.session?.user?.id ?? null);
console.log("[import] userId prop =", userId);
console.log("[import] bankAccountId =", bankAccountId);
console.log("[import] file =", file?.name ?? null);
console.log("[import] progressId =", progress.id);

      const { data, error: functionError } = await supabase.functions.invoke(
  "bank-transactions-import-csv",
  {
    body: formData,
    headers: accessToken
      ? { Authorization: `Bearer ${accessToken}` }
      : undefined,
  }
);

      if (functionError) throw functionError;

      const inserted = data?.inserted_count ?? data?.transaction_count ?? 0;
      const parsed = data?.parsed_count ?? null;
      const duplicates = data?.duplicate_count ?? 0;

      if (inserted > 0 && duplicates > 0) {
        toast.success(`Imported ${inserted} new transactions (${duplicates} duplicates skipped)`);
      } else if (inserted > 0) {
        toast.success(`Imported ${inserted} transactions`);
      } else if (parsed !== null && parsed > 0) {
        toast.info(`No new transactions — all ${parsed} are duplicates`);
      } else {
        toast.info(`No transactions found in file`);
      }

      onImportSuccess?.();

      setLocalFinished("done");

      // allow selecting same file again
      setFile(null);
      clearFileInput();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Import failed";
      setLocalFinished("error");
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (loading) return;
        if (!next) {
          closeDialog();
          return;
        }
        onOpenChange(true);
      }}
    >
      <DialogContent
        className="sm:max-w-md"
        onInteractOutside={(e) => {
          if (loading) e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          if (loading) e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>Import Bank Statements</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* BANK SELECT */}
          <div>
            <label className="text-sm font-medium mb-1 block">Bank Account</label>

            <Select
              value={bankAccountId}
              onValueChange={(v) => {
                setBankAccountId(v);
                // switching account should reset "finished" state & old progress
                setProgressId(null);
                setLocalFinished(null);
              }}
              disabled={loading}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select bank account" />
              </SelectTrigger>
              <SelectContent>
                {bankAccounts.map((acc) => {
                  const hasParser = supportedParserIds.has(acc.id);

                  return (
                    <SelectItem key={acc.id} value={acc.id}>
                      <div className="flex w-full items-center justify-between gap-3">
                        <span className="truncate">{acc.name}</span>

                        {hasParser && (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-green-600">
                            <span aria-hidden>✓</span>
                            Created
                          </span>
                        )}
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>

            </Select>

            {bankAccountId && SAMPLE_TEMPLATES[bankAccountId] && (
              <div className="mt-2 flex items-center justify-between gap-3 rounded-md border bg-muted/30 p-3">
                <div className="text-xs text-muted-foreground">
                  Need the right format? Download the sample for{" "}
                  <span className="font-medium text-foreground">
                    {SAMPLE_TEMPLATES[bankAccountId].parserName}
                  </span>
                  .
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => downloadCsv(SAMPLE_TEMPLATES[bankAccountId])}
                  disabled={loading}
                >
                  <Download className="mr-2 h-4 w-4" />
                  Sample CSV
                </Button>
              </div>
            )}
          </div>

          {/* FILE INPUT */}
          <div>
            <label className="text-sm font-medium mb-1 block">Statement File</label>

            <div
              role="button"
              tabIndex={0}
              onClick={() => !loading && fileInputRef.current?.click()}
              onKeyDown={(e) => {
                if ((e.key === "Enter" || e.key === " ") && !loading) {
                  e.preventDefault();
                  fileInputRef.current?.click();
                }
              }}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              className={`rounded-lg border-2 border-dashed p-6 text-center transition-colors ${
                loading
                  ? "cursor-not-allowed opacity-60"
                  : isDragOver
                    ? "cursor-pointer border-primary bg-primary/5"
                    : "cursor-pointer border-border hover:border-primary/60 hover:bg-muted/40"
              }`}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.tsv,.xlsx,.xls,text/csv,text/tab-separated-values,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/octet-stream"
                disabled={loading}
                className="sr-only"
                onChange={(e) => acceptStatementFile(e.target.files?.[0] ?? null)}
              />

              {file ? (
                <FileSpreadsheet className="mx-auto mb-3 h-9 w-9 text-primary" />
              ) : (
                <Upload className={`mx-auto mb-3 h-9 w-9 ${isDragOver ? "text-primary" : "text-muted-foreground"}`} />
              )}

              <p className="text-sm font-medium">
                {isDragOver ? "Drop file here" : file ? file.name : "Click to upload or drag and drop"}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                CSV, TSV, XLSX, or XLS files
              </p>
            </div>

            {(loading || progressRow || localFinished) && (
              <ImportProgressPanel
                status={
                  progressRow?.status ??
                  (loading
                    ? "queued"
                    : localFinished === "done"
                      ? "done"
                      : localFinished === "error"
                        ? "error"
                        : "queued")
                }
                message={progressRow?.message ?? null}
                fileName={progressRow?.file_name ?? file?.name ?? null}
                totalRows={progressRow?.total_rows ?? 0}
                processedRows={progressRow?.processed_rows ?? 0}
                parsedCount={progressRow?.parsed_count ?? 0}
                insertedCount={progressRow?.inserted_count ?? 0}
                dupFile={progressRow?.duplicates_in_file ?? 0}
                dupDb={progressRow?.duplicates_in_db ?? 0}
              />
            )}

            {file && (
              <p className="text-xs text-muted-foreground mt-1">
                Selected: {file.name}
              </p>
            )}
          </div>

          {/* After finish */}
          {isFinished && (
            <Button
              type="button"
              variant="outline"
              onClick={resetForAnotherFile}
              disabled={loading}
              className="w-full"
            >
              Import another file
            </Button>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={closeDialog} disabled={loading}>
            {isFinished ? "Close" : "Cancel"}
          </Button>

          <Button onClick={handleImport} disabled={!canImport}>
            {loading ? "Importing..." : isDone ? "Imported" : isError ? "Failed" : "Import"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
